/**
 * useBackgroundPreference — dashboard background mode, persisted per device.
 *
 * Modes (issue #414):
 *  - 'auto'     (default) — background follows the active sidebar section
 *                (see utils/backgroundSection.js), e.g. Operaciones -> sala
 *                limpia, Comercial -> comercial, Almacén -> almacén,
 *                Administración -> técnico, everything else -> técnico.
 *  - 'animated' — Remotion "pulsos de energía" loop on every section.
 *  - 'solid'    — flat navy, maximum legibility/performance.
 *  - 'image'    — pin one specific section image on every page
 *                (`fixedImageId`: 'cleanroom' | 'comercial' | 'almacen' | 'technical').
 *
 * MVP persistence: localStorage (per device). Migrating to a per-user DB
 * preference later just means swapping the read/write in this hook for a
 * profile field + mutation — nothing else in the tree needs to change since
 * everything else consumes this hook via BackgroundPreferenceContext.
 *
 * @module hooks/useBackgroundPreference
 */

import { useCallback, useState } from 'react';

export const BACKGROUND_STORAGE_KEY = 'promedic.dashboardBackgroundPreference';

export const BACKGROUND_MODES = {
  AUTO: 'auto',
  ANIMATED: 'animated',
  SOLID: 'solid',
  IMAGE: 'image',
};

/** Section images available when pinning a fixed background ('image' mode). */
export const SECTION_IMAGE_OPTIONS = [
  { id: 'cleanroom', label: 'Sala Limpia', description: 'Manufactura médica de precisión (Operaciones).' },
  { id: 'comercial', label: 'Comercial', description: 'Ambiente comercial y de negocios.' },
  { id: 'almacen', label: 'Almacén', description: 'Bodega e inventario.' },
  { id: 'technical', label: 'Técnico', description: 'Circuitería abstracta azul marino (Administración).' },
];

/** Mode options rendered by the Settings selector. */
export const BACKGROUND_MODE_OPTIONS = [
  {
    id: BACKGROUND_MODES.AUTO,
    label: 'Automático por sección',
    description: 'El fondo cambia según el módulo que estás usando.',
  },
  {
    id: BACKGROUND_MODES.ANIMATED,
    label: 'Animado',
    description: 'Pulsos de energía sutiles en loop, en toda la app.',
  },
  {
    id: BACKGROUND_MODES.SOLID,
    label: 'Sólido',
    description: 'Navy plano — máxima legibilidad y rendimiento.',
  },
  {
    id: BACKGROUND_MODES.IMAGE,
    label: 'Fijar imagen',
    description: 'Usa una sola imagen de sección en toda la app.',
  },
];

const DEFAULT_PREFERENCE = { mode: BACKGROUND_MODES.AUTO, fixedImageId: 'technical' };

const isValidMode = (mode) => Object.values(BACKGROUND_MODES).includes(mode);
const isValidImageId = (id) => SECTION_IMAGE_OPTIONS.some((opt) => opt.id === id);

const readStoredPreference = () => {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCE;
  try {
    const raw = window.localStorage.getItem(BACKGROUND_STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCE;
    const parsed = JSON.parse(raw);
    const mode = isValidMode(parsed?.mode) ? parsed.mode : DEFAULT_PREFERENCE.mode;
    const fixedImageId = isValidImageId(parsed?.fixedImageId)
      ? parsed.fixedImageId
      : DEFAULT_PREFERENCE.fixedImageId;
    return { mode, fixedImageId };
  } catch (err) {
    // Corrupt value / localStorage unavailable (private mode, quota) — safe default.
    return DEFAULT_PREFERENCE;
  }
};

const writeStoredPreference = (preference) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BACKGROUND_STORAGE_KEY, JSON.stringify(preference));
  } catch (err) {
    // localStorage unavailable — preference just won't persist this session.
  }
};

/**
 * @returns {{
 *   preference: {mode: string, fixedImageId: string},
 *   setMode: (mode: string) => void,
 *   setFixedImageId: (id: string) => void,
 * }}
 */
export const useBackgroundPreference = () => {
  const [preference, setPreference] = useState(readStoredPreference);

  const setMode = useCallback((mode) => {
    if (!isValidMode(mode)) return;
    setPreference((prev) => {
      const next = { ...prev, mode };
      writeStoredPreference(next);
      return next;
    });
  }, []);

  const setFixedImageId = useCallback((fixedImageId) => {
    if (!isValidImageId(fixedImageId)) return;
    setPreference((prev) => {
      const next = { ...prev, fixedImageId, mode: BACKGROUND_MODES.IMAGE };
      writeStoredPreference(next);
      return next;
    });
  }, []);

  return { preference, setMode, setFixedImageId };
};

export default useBackgroundPreference;
